import { isNil } from 'lodash';
import { useContext, useState } from 'react';
import {
  Dimensions,
  Image,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { MBView } from '@shared/components/MBView';
import { CardThemeContext } from '@shared/components/blocks/contexts/theme';
import { FileUpload } from '@shared/components/blocks/types';
import { MBClearButton } from '@shared/components/button';
import { MBLoadingIndicator } from '@shared/components/loading';
import { AnchoredTooltip } from '@shared/components/tooltip';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { useImageSrcAndDimensions, useMountedRef } from '@shared/util/hooks';

interface Props {
  image: FileUpload;
  editable: boolean;
  onPress?: VoidFunction;
  onClear?: VoidFunction;
  maxHeight?: number;
  maxWidth?: number;
  style?: StyleProp<ViewStyle>;
}

export function ImageItem({
  image,
  editable,
  onPress,
  onClear,
  maxWidth = Dimensions.get('window').width,
  maxHeight = Dimensions.get('window').height,
  style,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const stale = useContext(CardThemeContext) === 'stale';
  const [imgLoaded, setImgLoaded] = useState(false);
  const mounted = useMountedRef();

  const { src, width, height, ratio } = useImageSrcAndDimensions({
    image,
    containerWidth: maxWidth,
    containerHeight: maxHeight,
    grayscale: stale,
    size: 'contain',
  });

  const onLoad = () => {
    if (mounted.current) {
      setImgLoaded(true);
    }
  };

  if (!width || !height) {
    return <ImageLoadingIndicator width={maxWidth} height={maxHeight} />;
  }

  return (
    <TouchableOpacity onPress={onPress} style={{ marginRight: grid(1) }}>
      <AnchoredTooltip tooltip={image.name}>
        <MBView
          style={[
            {
              width: maxHeight * ratio,
              maxWidth,
              height: maxHeight,
              backgroundColor: colors.dropdown.backgroundNoSelection,
              borderRadius: grid(1),
              overflow: 'hidden',
              justifyContent: 'center',
            },
            style,
          ]}
          centerHorizontally
        >
          <Image
            source={{ uri: src }}
            onLoad={onLoad}
            style={{ width, height }}
            resizeMode={'contain'}
          />
          {!imgLoaded && <ImageLoadingIndicator width={width} height={height} />}
        </MBView>
        {editable && !isNil(onClear) && (
          <TouchableOpacity style={styles.clearButton} onPress={event => event.stopPropagation()}>
            <MBClearButton onPress={onClear} color={colors.foregroundInactive} />
          </TouchableOpacity>
        )}
      </AnchoredTooltip>
    </TouchableOpacity>
  );
}

function ImageLoadingIndicator({ width, height }: { width: number; height: number }) {
  return (
    <MBView fill center style={[styles.loadingIndicator, { width, height }]}>
      <MBLoadingIndicator />
    </MBView>
  );
}

const styles = StyleSheet.create({
  loadingIndicator: {
    borderRadius: grid(1),
    borderColor: Colors.v2.gray50_25,
    borderWidth: 1,
    marginRight: grid(1),
  },
  clearButton: {
    position: 'absolute',
    right: -grid(0.5),
    top: -grid(0.5),
    zIndex: 1,
  },
});
